import React from 'react';
import { useNavigate } from 'react-router-dom';

interface StorageLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  usedGB: number;
  limitGB: number;
}

const StorageLimitModal: React.FC<StorageLimitModalProps> = ({
  isOpen,
  onClose,
  usedGB,
  limitGB,
}) => {
  const navigate = useNavigate();
  if (!isOpen) return null;

  const usagePercent = Math.min((usedGB / limitGB) * 100, 100);

  const handleClearSpace = () => {
    onClose();
    navigate('/library');
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.iconWrapper}>📦</div>
        <h2 style={styles.title}>You're out of storage</h2>
        <p style={styles.subtitle}>
          You've used <strong style={styles.highlight}>{usedGB.toFixed(1)} GB</strong> of your{' '}
          <strong style={styles.highlight}>{limitGB} GB</strong> limit.
          Delete some files to continue uploading.
        </p>
        <div style={styles.progressWrapper}>
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${usagePercent}%` }} />
          </div>
          <div style={styles.progressLabels}>
            <span style={styles.progressText}>{usedGB.toFixed(1)} GB used</span>
            <span style={styles.progressText}>{limitGB} GB limit</span>
          </div>
        </div>
        <div style={styles.tipsBox}>
          <p style={styles.tipsTitle}>How to free up space:</p>
          {[
            '🗑️ Delete files you no longer need',
            '📁 Remove old or duplicate files',
            '⬇️ Download large files then delete them',
            '⭐ Check your Library — sort by size to find the biggest files',
          ].map((tip, i) => (
            <div key={i} style={styles.tipItem}>{tip}</div>
          ))}
        </div>
        <div style={styles.actions}>
          <button style={styles.primaryBtn} onClick={handleClearSpace}>Go to Library →</button>
          <button style={styles.secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px' },
  modal: { background: '#1A1F3A', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '20px', padding: '40px', maxWidth: '460px', width: '100%', boxShadow: '0 25px 60px rgba(0,0,0,0.5)' },
  iconWrapper: { fontSize: '52px', textAlign: 'center', marginBottom: '16px' },
  title: { color: '#E6E8F0', fontSize: '24px', fontWeight: 700, margin: '0 0 12px', textAlign: 'center' },
  subtitle: { color: '#8B92B0', fontSize: '15px', lineHeight: 1.6, margin: '0 0 24px', textAlign: 'center' },
  highlight: { color: '#E6E8F0' },
  progressWrapper: { marginBottom: '24px' },
  progressTrack: { background: '#252B4A', borderRadius: '999px', height: '10px', overflow: 'hidden', marginBottom: '8px' },
  progressFill: { height: '100%', borderRadius: '999px', background: 'linear-gradient(90deg, #EF4444, #F87171)', transition: 'width 0.3s ease' },
  progressLabels: { display: 'flex', justifyContent: 'space-between' },
  progressText: { color: '#6B7280', fontSize: '12px' },
  tipsBox: { background: '#252B4A', borderRadius: '12px', padding: '20px', marginBottom: '28px' },
  tipsTitle: { color: '#E6E8F0', fontSize: '13px', fontWeight: 600, margin: '0 0 12px' },
  tipItem: { color: '#8B92B0', fontSize: '13px', marginBottom: '8px', lineHeight: 1.5 },
  actions: { display: 'flex', gap: '12px' },
  primaryBtn: { flex: 1, background: 'linear-gradient(135deg, #9D4EDD, #00F5FF)', color: '#fff', border: 'none', borderRadius: '10px', padding: '13px 20px', fontSize: '15px', fontWeight: 600, cursor: 'pointer' },
  secondaryBtn: { flex: 1, background: 'transparent', color: '#8B92B0', border: '1px solid #2A2F45', borderRadius: '10px', padding: '13px 20px', fontSize: '15px', fontWeight: 600, cursor: 'pointer' },
};

export default StorageLimitModal;
